import { addBusinessDays, format } from "date-fns";
import React, { useMemo } from "react";

import { frequencyToQuestionCount, selectReminderCadence } from "@performyard/engagement";
import { Text } from "@performyard/py-components";

import { useEngagementSurveyCustomQuestions } from "../../../../../../api/hooks/defs/engagement-survey-custom-questions";

type EngagementSettingsSurveyScheduleProps = {
	frequency: number;
	nextSurveyDate: string | undefined;
	questionCycle: number;
	factorsFrequency: number;
	enpsFrequency: number;
	customQuestionsFrequency: number;
	customQuestionsIncludeAll: boolean;
	additionalFeedbackQuestionFrequency: number;
	sendReminderEmails: boolean;
};

const EngagementSettingsSurveySchedule = ({
	frequency,
	nextSurveyDate,
	questionCycle,
	factorsFrequency,
	enpsFrequency,
	customQuestionsFrequency,
	customQuestionsIncludeAll,
	additionalFeedbackQuestionFrequency,
	sendReminderEmails,
}: EngagementSettingsSurveyScheduleProps) => {
	const { data: customQuestions, isLoading } = useEngagementSurveyCustomQuestions();

	const customQuestionsCount = useMemo(() => {
		return customQuestions?.results.filter((q) => q.enabled).length ?? 0;
	}, [JSON.stringify(customQuestions), isLoading]);

	const nextDate = useMemo(() => {
		return nextSurveyDate ? new Date(nextSurveyDate) : null;
	}, [nextSurveyDate]);

	const remindersCadence = useMemo(() => {
		return nextDate ? selectReminderCadence(frequency) : [];
	}, [nextDate, frequency]);

	const nextFourDates = useMemo(() => {
		if (!nextDate) {
			return [];
		}
		return [0, 1, 2, 3].map((i) => {
			const date = new Date(nextDate);
			date.setDate(date.getDate() + i * frequency);
			return date;
		});
	}, [nextDate, frequency]);

	if (!nextSurveyDate || !nextDate) {
		return null;
	}

	const displayQuestionFrequencies = (current: number) => {
		const baseQuestionsCount = frequencyToQuestionCount(frequency);
		const includeCustomQuestions = (questionCycle + current) % customQuestionsFrequency === 0;
		const includeAdditionalFeedbackQuestion = (questionCycle + current) % additionalFeedbackQuestionFrequency === 0;
		const additionalQuestionsCount =
			(includeCustomQuestions && customQuestionsIncludeAll ? customQuestionsCount : 0) +
			(includeAdditionalFeedbackQuestion ? 1 : 0);
		const numberOfQuestions = baseQuestionsCount + additionalQuestionsCount;

		const factorsEnabled = (questionCycle + current) % factorsFrequency === 0;
		const enpsEnabled = (questionCycle + current) % enpsFrequency === 0;

		if (factorsFrequency === 1) {
			if (enpsEnabled) {
				return `eNPS + Factors${includeCustomQuestions ? " + Custom" : ""}${
					includeAdditionalFeedbackQuestion ? " + Feedback" : ""
				}, ${numberOfQuestions} Questions`;
			}
			return `Factors${includeCustomQuestions ? " + Custom" : ""}${
				includeAdditionalFeedbackQuestion ? " + Feedback" : ""
			}, ${numberOfQuestions} Questions`;
		} else if (enpsFrequency === 1) {
			if (factorsEnabled) {
				return `eNPS + Factors${includeCustomQuestions ? " + Custom" : ""}${
					includeAdditionalFeedbackQuestion ? " + Feedback" : ""
				}, ${numberOfQuestions} Questions`;
			} else if (includeCustomQuestions) {
				return `eNPS${customQuestionsIncludeAll ? " + Custom" : ""}${
					includeAdditionalFeedbackQuestion ? " + Feedback" : ""
				}, ${additionalQuestionsCount + 1} Question${additionalQuestionsCount > 0 ? "s" : ""}`;
			}
			return `eNPS${includeAdditionalFeedbackQuestion ? " + Feedback" : ""}, ${
				additionalQuestionsCount + 1
			} Question${additionalQuestionsCount > 0 ? "s" : ""}`;
		} else {
			if (factorsEnabled && enpsEnabled) {
				return `eNPS + Factors${includeCustomQuestions ? " + Custom" : ""}${
					includeAdditionalFeedbackQuestion ? " + Feedback" : ""
				}, ${numberOfQuestions} Questions`;
			} else if (factorsEnabled) {
				return `Factors${includeCustomQuestions ? " + Custom" : ""}${
					includeAdditionalFeedbackQuestion ? " + Feedback" : ""
				}, ${numberOfQuestions} Questions`;
			} else if (enpsEnabled) {
				const enpsQuestionCount = additionalQuestionsCount + 1;
				return `eNPS${includeCustomQuestions ? (customQuestionsIncludeAll ? " + Custom" : "") : ""}${
					includeAdditionalFeedbackQuestion ? " + Feedback" : ""
				}, ${enpsQuestionCount} Question${enpsQuestionCount > 1 ? "s" : ""}`;
			} else if (includeCustomQuestions || includeAdditionalFeedbackQuestion) {
				return `${includeCustomQuestions ? (customQuestionsIncludeAll ? "Custom" : "") : ""}${
					includeCustomQuestions && includeAdditionalFeedbackQuestion ? " + " : ""
				}${includeAdditionalFeedbackQuestion ? "Feedback" : ""}, ${additionalQuestionsCount} Question${
					additionalQuestionsCount > 1 ? "s" : ""
				}`;
			}
			return "No questions";
		}
	};

	return (
		<div
			className="flex w-full flex-col gap-2 rounded-[4px] bg-neutral-2 p-2"
			data-testid="engagement-settings-survey-schedule"
		>
			{/* Survey Schedule Section */}
			<div>
				<div className="flex gap-1 whitespace-nowrap">
					<Text size="caption" weight="semi-bold" color="neutral-9">
						Pulse Survey Schedule:
					</Text>
				</div>
				{nextFourDates.map((date, i) => {
					return (
						<div key={`${date.toString()}-${i}`} className="flex items-center gap-2 pl-2">
							<Text size="caption" weight="regular" color="neutral-9">
								•
							</Text>
							<Text size="caption" weight="regular" color="neutral-9">
								{format(date, "EEEE MMM d, yyyy")} ({displayQuestionFrequencies(i)})
							</Text>
						</div>
					);
				})}
			</div>

			{/* Reminder Schedule Section - Only show if reminders are enabled */}
			{sendReminderEmails && frequency && nextSurveyDate && (
				<div>
					<Text size="caption" weight="semi-bold" color="neutral-9">
						Next Pulse Survey Reminder Schedule:{" "}
					</Text>
					{remindersCadence.length > 0 && (
						<div className="flex items-center gap-2 pl-2">
							<Text size="caption" weight="semi-bold" color="neutral-9" overrideElement="span">
								• First Reminder:{" "}
							</Text>{" "}
							<Text size="caption" weight="regular" color="neutral-9" overrideElement="span">
								{format(addBusinessDays(nextDate, remindersCadence[0]), "EEEE, MMM d, yyyy")}
							</Text>
						</div>
					)}
					{remindersCadence.length > 1 && (
						<div className="flex items-center gap-2 pl-2">
							<Text size="caption" weight="semi-bold" color="neutral-9" overrideElement="span">
								• Second Reminder:{" "}
							</Text>{" "}
							<Text size="caption" weight="regular" color="neutral-9" overrideElement="span">
								{format(addBusinessDays(nextDate, remindersCadence[1]), "EEEE, MMM d, yyyy")}
							</Text>
						</div>
					)}
					{remindersCadence.length > 2 && (
						<div className="flex items-center gap-2 pl-2">
							<Text size="caption" weight="semi-bold" color="neutral-9" overrideElement="span">
								• Third Reminder:{" "}
							</Text>{" "}
							<Text size="caption" weight="regular" color="neutral-9" overrideElement="span">
								{format(addBusinessDays(nextDate, remindersCadence[2]), "EEEE, MMM d, yyyy")}
							</Text>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default EngagementSettingsSurveySchedule;
